/* INNERDAY gut quiz: content, branching, and result rules shared by the quiz and the guide page. */
(function () {
  'use strict';

  var QUIZ_VERSION = '1.0';
  var ROUTING_VERSION = '1.0';
  var CONTENT_VERSION = '1.0';
  var NOTICE_VERSION = 'draft-2026-10-18';
  var STORE_KEY = 'innerday.gutQuiz.v1';

  var EXPERTS = {
    helen: {
      name: 'Dr. Helen Ward',
      title: 'Gastroenterologist',
      image: 'helen-ward.webp',
      hover: 'Bloating, trapped gas, or that never-quite-finished feeling? I’ll help you make sense of the patterns you’ve noticed, explore common questions about IBS and SIBO, and understand what deserves a closer look.'
    },
    maya: {
      name: 'Maya Chen',
      title: 'Registered Dietitian · Digestive Health',
      image: 'maya-chen.webp',
      hover: 'Eating ‘healthy’ but still feeling uncomfortable? I’ll walk you through food triggers, fibre, and everyday habits—so you can approach your next meal with a clearer plan instead of another list of foods to avoid.'
    },
    claire: {
      name: 'Dr. Claire Bennett',
      title: 'Clinical Psychologist · Gut–Brain Health',
      image: 'claire-bennett.webp',
      hover: 'When your gut feels unpredictable, meals out and everyday plans can become stressful. I’ll help you explore the gut–brain connection and practical ways to handle the worry around symptoms, while taking what you’re feeling seriously.'
    },
    daniel: {
      name: 'Dr. Daniel Mercer',
      title: 'Nutrition Scientist · Supplement Research',
      image: 'daniel-mercer.webp',
      hover: 'Another probiotic? More fibre? A digestive enzyme? I’ll help you understand the differences, what the evidence supports, and what to look for in a formula—so choosing digestive support feels less overwhelming.'
    }
  };

  var PARTS = [
    { part: 1, expert: 'helen', title: 'How your gut feels', questions: ['Q01', 'Q02', 'Q03', 'Q04'] },
    { part: 2, expert: 'maya', title: 'Your meals & daily patterns', questions: ['Q05', 'Q06', 'Q07'] },
    { part: 3, expert: 'claire', title: 'Your gut & everyday life', questions: ['Q08', 'Q09', 'Q10'] },
    { part: 4, expert: 'daniel', title: 'What you’ve tried & what comes next', questions: ['Q11', 'Q12', 'Q13'] }
  ];

  function o(id, label, extra) {
    var option = { id: id, label: label };
    if (extra) Object.keys(extra).forEach(function (key) { option[key] = extra[key]; });
    return option;
  }

  var QUESTIONS = {
    Q01: {
      type: 'single',
      text: 'What would you most like help understanding about your gut?',
      helper: 'Choose what bothers you most. We’ll make room for other symptoms too.',
      why: '“My digestion feels off” can mean very different things. Starting with what bothers you most helps us choose the questions that follow.',
      options: [
        o('bloating', 'My belly feels bloated or full of gas.'),
        o('bowel_difficulty', 'I feel backed up, or unfinished after going.'),
        o('loose_urgency', 'I get loose stools or need to rush to the bathroom.'),
        o('meal_discomfort', 'Meals leave me feeling uncomfortably full or heavy.'),
        o('mixed', 'Several of these. I can’t pick just one.'),
        o('other', 'My main concern isn’t listed.'),
        o('education', 'I don’t have symptoms. I’m here to learn.')
      ]
    },
    Q02A: {
      type: 'multi',
      text: 'When you say “bloated,” what do you notice?',
      helper: 'Select all that apply.',
      why: 'Feeling bloated, seeing your belly expand, and passing gas are different experiences. Knowing which you mean helps us make your guide more relevant.',
      options: [
        o('pressure', 'My belly feels tight, swollen, or under pressure.'),
        o('visible_swelling', 'My belly looks visibly bigger.'),
        o('passing_gas', 'I’m passing more gas.'),
        o('burping', 'I’m burping more.'),
        o('unsure', 'I’m not sure how to describe it.', { exclusive: true })
      ]
    },
    Q02B: {
      type: 'multi',
      text: 'What makes your bathroom visits frustrating?',
      helper: 'Select all that apply.',
      why: 'How often you go is only part of the picture. Stool consistency, effort, and that unfinished feeling matter too, even if you go every day.',
      options: [
        o('infrequent', 'I go fewer than three times a week.'),
        o('hard_stool', 'My stools are hard, dry, or lumpy.'),
        o('straining', 'I need to strain or struggle to go.'),
        o('incomplete', 'I still feel like there’s more left afterwards.'),
        o('unsure', 'I’m not sure.', { exclusive: true })
      ]
    },
    Q02C: {
      type: 'multi',
      text: 'Which details sound familiar?',
      helper: 'Select all that apply.',
      why: 'Loose stools, urgent trips, and repeated visits don’t describe exactly the same thing. We also want to know if your bowel pattern changes from day to day.',
      options: [
        o('loose_stool', 'My stools are loose or watery.'),
        o('urgency', 'I get sudden urges that are difficult to hold.'),
        o('repeated_visits', 'I make several bathroom visits close together.'),
        o('alternating_difficulty', 'On other days, my stools are hard or difficult to pass.'),
        o('unsure', 'I’m not sure.', { exclusive: true })
      ]
    },
    Q02D: {
      type: 'multi',
      text: 'What feels uncomfortable around mealtimes?',
      helper: 'Select all that apply.',
      why: 'Feeling full very early and feeling uncomfortable after finishing a meal are different details. Your guide should reflect the experience you’re actually having.',
      options: [
        o('early_fullness', 'I feel full before I’ve eaten much.'),
        o('postmeal_fullness', 'I feel uncomfortably full after eating.'),
        o('upper_discomfort', 'I feel pressure or burning in my upper belly.'),
        o('nausea', 'I feel nauseous.'),
        o('burping_gas', 'I notice more burping or gas.'),
        o('unsure', 'I’m not sure.', { exclusive: true })
      ]
    },
    Q02E: {
      type: 'multi',
      text: 'Let’s unpack that. What have you been noticing?',
      helper: 'Select all that apply.',
      why: 'When several things are happening, it helps to name each one. We’ll keep those details together rather than squeezing your experience into a single symptom.',
      options: [
        o('bloating', 'Bloating, pressure, or visible swelling.'),
        o('gas_burping', 'Gas or burping.'),
        o('bowel_difficulty', 'Hard or difficult bowel movements.'),
        o('incomplete', 'Feeling unfinished after going.'),
        o('loose_urgency', 'Loose stools or urgent bathroom visits.'),
        o('meal_discomfort', 'Feeling full too early or uncomfortable after meals.'),
        o('other', 'Something else.', { field: 'Tell us a little more, if you’d like.' })
      ]
    },
    Q03: {
      type: 'single',
      text: 'Over the past 14 days, on how many days has this bothered you?',
      textMixed: 'Over the past 14 days, on how many days have any of these symptoms bothered you?',
      why: 'Something that happens occasionally can need a different starting point from something that keeps returning. This helps us describe how often symptoms have been part of your recent days.',
      options: [
        o('days_0', '0 days'),
        o('days_1_3', '1 to 3 days'),
        o('days_4_7', '4 to 7 days'),
        o('days_8_11', '8 to 11 days'),
        o('days_12_14', '12 to 14 days')
      ]
    },
    Q04: {
      type: 'single',
      text: 'When did you first start noticing this problem?',
      helper: 'If it comes and goes, think about when you first noticed the pattern.',
      why: 'A recent change and a pattern you’ve lived with for years give us different context. This helps us decide whether your guide should emphasise recent changes, an ongoing pattern, or both.',
      options: [
        o('under_2_weeks', 'Less than 2 weeks ago.'),
        o('weeks_2_to_under_3_months', '2 weeks to less than 3 months ago.'),
        o('months_3_to_under_1_year', '3 months to less than 1 year ago.'),
        o('year_plus', 'A year ago or longer.'),
        o('unsure', 'I can’t remember.')
      ]
    },
    Q05: {
      type: 'multi',
      text: 'On days when your gut bothers you, when do you notice it most?',
      textPast: 'Thinking back to when you’ve had symptoms, when did you notice them most?',
      helper: 'Select all that apply.',
      why: 'Symptoms before breakfast, after a meal, or around a bathroom visit give us different details to work with. Timing helps us choose what would be useful for you to record.',
      options: [
        o('waking', 'When I wake up, before eating.'),
        o('around_meals', 'During or soon after a meal.'),
        o('later_day', 'Later in the day, after several meals.'),
        o('around_bowel_movement', 'Around a bathroom visit.'),
        o('throughout_day', 'It comes and goes throughout the day.'),
        o('no_pattern', 'I haven’t noticed a clear pattern.', { exclusive: true })
      ]
    },
    Q06A: {
      type: 'single',
      text: 'After a bowel movement, how does your gut usually feel?',
      why: 'This is about what changes after you go, which is a different detail from how the visit itself feels.',
      options: [
        o('better', 'More comfortable, at least temporarily.'),
        o('unchanged', 'About the same.'),
        o('worse', 'More uncomfortable.'),
        o('varies', 'It varies.'),
        o('unsure', 'I’m not sure.')
      ]
    },
    Q06B: {
      type: 'single',
      text: 'When your gut feels uncomfortable, what are your bowel movements usually like?',
      why: 'Bloating and discomfort around meals can happen alongside changes in bathroom habits. We want to include that part of your experience too.',
      options: [
        o('difficult', 'Hard, difficult to pass, or unfinished.'),
        o('loose_urgent', 'Loose, watery, or urgent.'),
        o('alternating', 'It alternates between the two.'),
        o('comfortable', 'Formed, easy to pass, and comfortable.'),
        o('unsure', 'I haven’t noticed, or I’m not sure.')
      ]
    },
    Q07: {
      type: 'single',
      text: 'Have you noticed a connection between particular foods or drinks and your symptoms?',
      why: 'A repeated observation and an uncertain food restriction need different guidance. This helps us focus on what you’ve noticed without treating every uncomfortable meal as proof of a food intolerance.',
      options: [
        o('repeated', 'The same foods or drinks have repeatedly been followed by symptoms.', { field: 'Which foods or drinks have you noticed?' }),
        o('inconsistent', 'Sometimes, but it isn’t consistent.', { field: 'Which foods or drinks have you noticed?' }),
        o('restriction_unclear', 'I’ve cut things out, but I’m not sure it helped.', { field: 'What have you reduced or cut out?' }),
        o('no_connection', 'I haven’t noticed a connection.'),
        o('not_observed', 'I haven’t paid much attention to it.')
      ]
    },
    Q08: {
      type: 'single',
      text: 'Compared with calmer days, how does your gut tend to feel when you’re under pressure?',
      why: 'We’re checking whether you’ve noticed a difference during stressful periods, alongside the food and bathroom patterns you’ve already shared. We won’t assume stress explains your symptoms.',
      options: [
        o('worse', 'More uncomfortable.'),
        o('unchanged', 'About the same.'),
        o('better', 'Less uncomfortable.'),
        o('unsure', 'I’m not sure, or I haven’t had enough of a comparison.'),
        o('withheld', 'I’d prefer not to say.')
      ]
    },
    Q09: {
      type: 'single',
      text: 'Over the past 14 days, how often have you worried that your gut might disrupt something you planned?',
      helper: 'Think about meals, journeys, work, or spending time with other people.',
      why: 'Earlier, we asked how often symptoms happened. This asks how often the possibility of symptoms occupied your attention, even before anything happened.',
      options: [
        o('days_0', 'Never.'),
        o('days_1_3', 'On 1 to 3 days.'),
        o('days_4_7', 'On 4 to 7 days.'),
        o('days_8_14', 'On 8 to 14 days.'),
        o('withheld', 'I’d prefer not to say.')
      ]
    },
    Q10: {
      type: 'multi',
      text: 'Over the past 14 days, have your gut symptoms, or worries about them, changed what you do?',
      helper: 'Select all that apply.',
      why: 'Symptom frequency doesn’t tell us everything about the impact. Knowing what has changed in your day helps us make the guidance more practical.',
      options: [
        o('social_avoidance', 'I’ve avoided meals out or social plans.'),
        o('bathroom_planning', 'I’ve planned journeys or activities around bathroom access.'),
        o('meal_skipping', 'I’ve skipped or delayed meals because I was worried about symptoms.'),
        o('clothing', 'I’ve chosen different clothes because my belly felt uncomfortable.'),
        o('work_downtime', 'It’s been harder to concentrate at work or enjoy my downtime.'),
        o('none', 'None of these.', { exclusive: true }),
        o('withheld', 'I’d prefer not to say.', { exclusive: true })
      ]
    },
    Q11: {
      type: 'multi',
      text: 'What have you already tried specifically for these digestive symptoms?',
      helper: 'Select all that apply, including things you’ve stopped.',
      why: 'Starting fresh and having already tried several things are different experiences. Your guide should account for what you’ve done so far.',
      options: [
        o('diet_routine', 'Changing what, how much, or when I eat.'),
        o('fibre', 'A fibre supplement.'),
        o('probiotic', 'A probiotic.'),
        o('enzymes', 'Digestive enzymes.'),
        o('botanicals', 'Herbal teas, botanical supplements, or gut health blends.'),
        o('medicines', 'Prescription or over the counter medicines.'),
        o('other', 'Something else.', { field: 'What have you tried?' }),
        o('none', 'I haven’t tried anything specifically for this.', { exclusive: true }),
        o('unsure', 'I’m not sure.', { exclusive: true })
      ]
    },
    Q12A: {
      type: 'single',
      text: 'Overall, what has your experience been with the things you’ve tried?',
      helper: 'Choose the closest answer.',
      why: 'Lasting improvement, temporary relief, no clear change, and feeling worse call for different next steps. Your experience should shape what your guide focuses on.',
      note: { label: 'Anything you’d like to add about what happened?', helper: 'You can include what you tried, how long you tried it, and what you noticed.' },
      options: [
        o('settled', 'My symptoms have settled and haven’t returned.'),
        o('partial_help', 'Something has helped, but symptoms remain.'),
        o('temporary_help', 'Something helped initially, then the problems returned.'),
        o('no_help', 'I haven’t noticed a clear improvement.'),
        o('worse', 'I’ve sometimes felt worse while trying something.'),
        o('mixed', 'Different things have had different results.'),
        o('unsure', 'I’m not sure.')
      ]
    },
    Q12B: {
      type: 'multi',
      text: 'Around the time this first started, or noticeably changed, did any of these happen?',
      helper: 'Select all that apply.',
      why: 'Changes around the same time can be useful context. We’ll include them in your timeline without assuming they caused the symptoms.',
      options: [
        o('medicine_change', 'I started, stopped, or changed a medicine.'),
        o('supplement_change', 'I started a supplement or changed the amount I took.'),
        o('illness_antibiotics', 'I had a stomach illness or took antibiotics.'),
        o('travel_routine', 'I travelled or had a significant change in routine.'),
        o('eating_change', 'I changed how or what I ate.'),
        o('nothing_obvious', 'Nothing obvious comes to mind.', { exclusive: true }),
        o('unsure', 'I can’t remember.', { exclusive: true })
      ]
    },
    Q13: {
      type: 'multi',
      text: 'Is there anything else your guide should account for?',
      helper: 'Select all that apply.',
      why: 'Medicines, other supplements, a diagnosis, pregnancy, or an allergy can change what makes sense next. This keeps your guide realistic.',
      options: [
        o('current_medicines', 'I currently take prescription or over the counter medicines.'),
        o('current_supplements', 'I currently take supplements.'),
        o('condition_investigation', 'I have a diagnosed digestive condition or I’m currently being investigated for one.'),
        o('pregnancy_nursing_ttc', 'I’m pregnant, nursing, or trying to conceive.'),
        o('allergy', 'I have a food, plant, or supplement allergy.'),
        o('change_related', 'My symptoms started or changed around a medicine or supplement change.'),
        o('none', 'None of these.', { exclusive: true }),
        o('unsure', 'I’m not sure.', { exclusive: true }),
        o('withheld', 'I’d prefer not to say.', { exclusive: true })
      ]
    }
  };

  /* ---------- Answer access ---------- */

  function emptyState() {
    return {
      v: 1,
      sessionId: uid(),
      firstName: '',
      birthYear: null,
      consent: null,
      answers: {},
      screen: 'name',
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      result: null
    };
  }

  function uid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function load() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.v === 1 ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function save(state) {
    state.updatedAt = new Date().toISOString();
    window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
    return state;
  }

  function clear() {
    try { window.localStorage.removeItem(STORE_KEY); } catch (error) {}
  }

  function branchFor(slot, answers) {
    if (slot === 'Q02') {
      var q01 = single(answers, 'Q01');
      return {
        bloating: 'Q02A',
        bowel_difficulty: 'Q02B',
        loose_urgency: 'Q02C',
        meal_discomfort: 'Q02D',
        mixed: 'Q02E',
        other: 'Q02E'
      }[q01] || null;
    }
    if (slot === 'Q06') return bowelCaptured(answers) ? 'Q06A' : 'Q06B';
    if (slot === 'Q12') {
      var tried = selected(answers, 'Q11');
      if (!tried.length) return null;
      return tried.some(function (id) { return id !== 'none' && id !== 'unsure'; }) ? 'Q12A' : 'Q12B';
    }
    return slot;
  }

  function entry(answers, slot) {
    var stored = answers[slot];
    if (!stored) return null;
    var branch = branchFor(slot, answers);
    if (branch && stored.branch && stored.branch !== branch) return null;
    return stored;
  }

  function selected(answers, slot) {
    var stored = entry(answers, slot);
    return stored && stored.selected ? stored.selected.slice() : [];
  }

  function single(answers, slot) {
    return selected(answers, slot)[0] || null;
  }

  function has(answers, slot, id) {
    return selected(answers, slot).indexOf(id) !== -1;
  }

  function hasAny(answers, slot, ids) {
    return selected(answers, slot).some(function (id) { return ids.indexOf(id) !== -1; });
  }

  function text(answers, slot, id) {
    var stored = entry(answers, slot);
    if (!stored || !stored.text) return '';
    return (stored.text[id] || '').trim();
  }

  function branchOf(answers, slot) {
    var stored = entry(answers, slot);
    return stored ? stored.branch : null;
  }

  function bowelCaptured(answers) {
    var q01 = single(answers, 'Q01');
    if (q01 === 'bowel_difficulty' || q01 === 'loose_urgency') return true;
    if (branchFor('Q02', answers) === 'Q02E') {
      return hasAny(answers, 'Q02', ['bowel_difficulty', 'incomplete', 'loose_urgency']);
    }
    return false;
  }

  function question(slot, answers) {
    var branch = branchFor(slot, answers) || slot;
    var base = QUESTIONS[branch];
    if (!base) return null;
    var resolved = {
      slot: slot,
      branch: branch,
      type: base.type,
      text: base.text,
      helper: base.helper || '',
      why: base.why,
      options: base.options,
      note: base.note || null
    };
    if (slot === 'Q03' && (single(answers, 'Q01') === 'mixed' || single(answers, 'Q01') === 'other')) {
      resolved.text = base.textMixed;
    }
    if (slot === 'Q05' && single(answers, 'Q03') === 'days_0') resolved.text = base.textPast;
    return resolved;
  }

  function setAnswer(state, slot, branch, ids, texts) {
    var answers = state.answers;
    var before = single(answers, 'Q01');
    answers[slot] = { branch: branch, selected: ids, text: texts || {} };
    if (slot === 'Q01' && before && before !== ids[0]) {
      delete answers.Q02;
      delete answers.Q03;
      delete answers.Q04;
    }
    if (slot === 'Q01' && ids[0] === 'education') {
      ['Q02', 'Q03', 'Q04', 'Q05', 'Q06', 'Q07', 'Q08', 'Q09', 'Q10', 'Q11', 'Q12', 'Q13'].forEach(function (key) {
        delete answers[key];
      });
    }
    ['Q06', 'Q12'].forEach(function (key) {
      var stored = answers[key];
      if (stored && stored.branch && stored.branch !== branchFor(key, answers)) delete answers[key];
    });
    return state;
  }

  function isEducation(answers) {
    return single(answers, 'Q01') === 'education';
  }

  /* ---------- Phrasing ---------- */

  var DAYS = {
    days_1_3: '1–3',
    days_4_7: '4–7',
    days_8_11: '8–11',
    days_12_14: '12–14'
  };

  var WORRY_DAYS = { days_1_3: '1–3', days_4_7: '4–7', days_8_14: '8–14' };

  var MAIN_CONCERN = {
    bloating: 'bloating or gas',
    bowel_difficulty: 'feeling backed up or unfinished after going',
    loose_urgency: 'loose stools or urgent bathroom visits',
    meal_discomfort: 'feeling uncomfortably full or heavy after meals',
    mixed: 'several concerns together',
    other: 'a concern that isn’t listed'
  };

  var DETAIL = {
    Q02A: {
      pressure: 'tightness, swelling, or pressure in your belly',
      visible_swelling: 'a belly that looks visibly bigger',
      passing_gas: 'passing more gas',
      burping: 'burping more'
    },
    Q02B: {
      infrequent: 'going fewer than three times a week',
      hard_stool: 'hard, dry, or lumpy stools',
      straining: 'needing to strain to go',
      incomplete: 'feeling there’s more left afterwards'
    },
    Q02C: {
      loose_stool: 'loose or watery stools',
      urgency: 'sudden urges that are difficult to hold',
      repeated_visits: 'several bathroom visits close together',
      alternating_difficulty: 'hard or difficult stools on other days'
    },
    Q02D: {
      early_fullness: 'feeling full before you’ve eaten much',
      postmeal_fullness: 'feeling uncomfortably full after eating',
      upper_discomfort: 'pressure or burning in your upper belly',
      nausea: 'feeling nauseous',
      burping_gas: 'more burping or gas'
    },
    Q02E: {
      bloating: 'Bloating, pressure, or visible swelling',
      gas_burping: 'Gas or burping',
      bowel_difficulty: 'Hard or difficult bowel movements',
      incomplete: 'Feeling unfinished after going',
      loose_urgency: 'Loose stools or urgent bathroom visits',
      meal_discomfort: 'Feeling full too early or uncomfortable after meals',
      other: 'Something else'
    }
  };

  var TIMING = {
    waking: 'when you wake up, before eating',
    around_meals: 'during or soon after meals',
    later_day: 'later in the day, after several meals',
    around_bowel_movement: 'around bathroom visits',
    throughout_day: 'on and off throughout the day'
  };

  var DURATION = {
    under_2_weeks: 'less than 2 weeks ago',
    weeks_2_to_under_3_months: '2 weeks to less than 3 months ago',
    months_3_to_under_1_year: '3 months to less than a year ago',
    year_plus: 'a year ago or longer'
  };

  var AFTER_BOWEL = {
    better: 'Your gut feels more comfortable after a bowel movement, at least for a while.',
    unchanged: 'Your gut feels about the same after a bowel movement.',
    worse: 'Your gut feels more uncomfortable after a bowel movement.',
    varies: 'How your gut feels after a bowel movement varies.'
  };

  var BOWEL_PATTERN = {
    difficult: 'You described hard, difficult, or unfinished bowel movements when your gut feels uncomfortable.',
    loose_urgent: 'You described loose, watery, or urgent bowel movements when your gut feels uncomfortable.',
    alternating: 'Your bowel movements alternate between hard and loose when your gut feels uncomfortable.',
    comfortable: 'Your bowel movements stay formed and comfortable when your gut feels uncomfortable.'
  };

  var IMPACT_SENTENCE = {
    social_avoidance: 'You’ve avoided meals out or social plans.',
    bathroom_planning: 'You’ve planned journeys or activities around bathroom access.',
    meal_skipping: 'You’ve skipped or delayed meals because of worry about symptoms.',
    clothing: 'You’ve chosen different clothes because your belly felt uncomfortable.',
    work_downtime: 'It’s been harder to concentrate at work or enjoy your downtime.'
  };

  var IMPACT_PHRASE = {
    social_avoidance: 'whether you go to meals out or social plans',
    bathroom_planning: 'how you plan journeys around bathroom access',
    meal_skipping: 'when or whether you eat',
    clothing: 'what you wear',
    work_downtime: 'how you concentrate at work or enjoy your downtime'
  };

  var IMPACT_ORDER = ['social_avoidance', 'bathroom_planning', 'meal_skipping', 'work_downtime', 'clothing'];
  var SIGNIFICANT_IMPACT = ['social_avoidance', 'bathroom_planning', 'meal_skipping', 'work_downtime'];

  function joinList(items) {
    if (items.length <= 1) return items.join('');
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  }

  function capitalize(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
  }

  function withName(name, rest) {
    return name ? name + ', ' + rest : capitalize(rest);
  }

  /* ---------- Flags ---------- */

  function concernFlags(answers) {
    var q01 = single(answers, 'Q01');
    var branch = branchFor('Q02', answers);
    var q06 = branchOf(answers, 'Q06') === 'Q06B' ? single(answers, 'Q06') : null;
    var e = branch === 'Q02E';
    var flags = {
      bloating: q01 === 'bloating' || (e && hasAny(answers, 'Q02', ['bloating', 'gas_burping'])),
      bowelDifficulty:
        q01 === 'bowel_difficulty' ||
        (e && hasAny(answers, 'Q02', ['bowel_difficulty', 'incomplete'])) ||
        (branch === 'Q02C' && has(answers, 'Q02', 'alternating_difficulty')) ||
        q06 === 'difficult' ||
        q06 === 'alternating',
      looseUrgency:
        q01 === 'loose_urgency' ||
        (e && has(answers, 'Q02', 'loose_urgency')) ||
        q06 === 'loose_urgent' ||
        q06 === 'alternating',
      mealDiscomfort: q01 === 'meal_discomfort' || (e && has(answers, 'Q02', 'meal_discomfort')),
      alternating: q06 === 'alternating' || (branch === 'Q02C' && has(answers, 'Q02', 'alternating_difficulty'))
    };
    flags.bowel = flags.bowelDifficulty || flags.looseUrgency;
    return flags;
  }

  function concernList(answers) {
    var flags = concernFlags(answers);
    var q01 = single(answers, 'Q01');
    var list = [];
    if (q01 && q01 !== 'education' && q01 !== 'mixed' && q01 !== 'other') list.push(q01);
    if (flags.bloating && list.indexOf('bloating') === -1) list.push('bloating');
    if (flags.bowelDifficulty && list.indexOf('bowel_difficulty') === -1) list.push('bowel_difficulty');
    if (flags.looseUrgency && list.indexOf('loose_urgency') === -1) list.push('loose_urgency');
    if (flags.mealDiscomfort && list.indexOf('meal_discomfort') === -1) list.push('meal_discomfort');
    if (q01 === 'other' || has(answers, 'Q02', 'other')) list.push('other');
    return list;
  }

  function reviewFlags(answers) {
    var review =
      hasAny(answers, 'Q13', ['current_medicines', 'current_supplements', 'condition_investigation', 'pregnancy_nursing_ttc', 'allergy', 'change_related']) ||
      (branchOf(answers, 'Q12') === 'Q12B' && hasAny(answers, 'Q12', ['medicine_change', 'supplement_change']));
    var q13 = selected(answers, 'Q13');
    return {
      individualReview: review,
      previousWorsening: branchOf(answers, 'Q12') === 'Q12A' && single(answers, 'Q12') === 'worse',
      suitabilityIncomplete: !q13.length || hasAny(answers, 'Q13', ['unsure', 'withheld'])
    };
  }

  function impactSelected(answers) {
    var picked = selected(answers, 'Q10');
    return IMPACT_ORDER.filter(function (id) { return picked.indexOf(id) !== -1; });
  }

  /* ---------- Routing ---------- */

  function profileFor(answers) {
    var q03 = single(answers, 'Q03');
    var q09 = single(answers, 'Q09');
    var q10 = selected(answers, 'Q10');
    if (hasAny(answers, 'Q10', SIGNIFICANT_IMPACT) || q09 === 'days_8_14') return 'profile_impact';
    if (q03 === 'days_4_7' || q03 === 'days_8_11' || q03 === 'days_12_14') return 'profile_recurring';
    if (q03 === 'days_1_3' && (q09 === 'days_0' || q09 === 'days_1_3') && q10.length === 1 && q10[0] === 'none') {
      return 'profile_occasional';
    }
    return null;
  }

  function routeFor(answers) {
    var q01 = single(answers, 'Q01');
    if (!q01) return 'insufficient';
    if (q01 === 'education') return 'education';
    if (branchOf(answers, 'Q12') === 'Q12A' && single(answers, 'Q12') === 'settled') return 'settled';
    var q03 = single(answers, 'Q03');
    if (!q03) return 'insufficient';
    if (q03 === 'days_0') return 'no_recent';
    return 'symptomatic';
  }

  var STAGE_NAMES = {
    stage_1: 'Occasional discomfort',
    stage_2: 'Discomfort that keeps returning',
    stage_3: 'Getting in the way of everyday life'
  };

  var STAGE_CARDS = [
    { id: 'profile_occasional', stage: 'Stage 1', name: 'Occasional discomfort', line: 'A few off days' },
    { id: 'profile_recurring', stage: 'Stage 2', name: 'Discomfort that keeps returning', line: 'The pattern' },
    { id: 'profile_impact', stage: 'Stage 3', name: 'Getting in the way of everyday life', line: 'Part of the day' }
  ];

  var STAGE_PICTURES = {
    profile_occasional: {
      focus: 0,
      called: 'occasional discomfort.',
      body: 'Your gut has off days, then it settles again. The episodes are real. They just aren’t taking over most of your days yet.',
      chartLead: 'On the days you described, symptoms rise above your usual, then ease off.',
      noteBefore: 'It’s why a symptom can ',
      noteStrong: 'show up, then ease off',
      noteAfter: ' without anything else changing.'
    },
    profile_recurring: {
      focus: 1,
      called: 'discomfort that keeps returning.',
      body: 'This isn’t one off day. The same kind of discomfort is showing up often enough to be a pattern worth paying attention to.',
      chartLead: 'The uncomfortable days aren’t isolated. They keep coming back across the two weeks.',
      noteBefore: 'It’s why the same discomfort ',
      noteStrong: 'keeps coming back',
      noteAfter: ', even on days that start out fine.'
    },
    profile_impact: {
      focus: 2,
      called: 'getting in the way of everyday life.',
      body: 'It isn’t only how your gut feels. It’s the planning around it: meals, plans, clothes, or the day itself.',
      chartLead: 'The discomfort, or the worry about it, is taking up more of the day.',
      noteBefore: 'It’s why symptoms, or the worry about them, ',
      noteStrong: 'start changing your day',
      noteAfter: '.'
    }
  };

  function stagePicture(route, profile, stage) {
    var placed = route === 'symptomatic';
    var selected = placed
      ? (STAGE_PICTURES[profile] ? profile : stage === 'stage_3' ? 'profile_impact' : stage === 'stage_2' ? 'profile_recurring' : 'profile_occasional')
      : null;
    var picture = placed ? STAGE_PICTURES[selected] : {
      focus: -1,
      called: route === 'education' ? 'a starting point, not a symptom pattern.' : route === 'settled' ? 'a settled stretch.' : route === 'no_recent' ? 'a quiet stretch.' : 'an unclear picture.',
      body: route === 'education'
        ? 'You’re here to learn, rather than to sort out current symptoms. Nothing here says your gut is in trouble.'
        : route === 'settled'
          ? 'You said your symptoms have settled and haven’t returned. There’s nothing here that says you need to add something new.'
          : route === 'no_recent'
            ? 'You reported no symptom-days in the past 14 days. Your earlier experiences stay in view, without calling this a current pattern.'
            : 'Your answers don’t sit clearly in one pattern. Your guide stays with what you did share, rather than guessing the rest.',
      chartLead: 'These are the three patterns people describe. Yours isn’t marked, because your answers don’t place you in one.',
      noteBefore: 'If things change, the details worth noticing are ',
      noteStrong: 'when it happens, and what else is going on',
      noteAfter: '.'
    };
    return {
      placed: placed,
      selected: selected,
      focus: picture.focus,
      called: picture.called,
      body: picture.body,
      chartLead: picture.chartLead,
      noteBefore: picture.noteBefore,
      noteStrong: picture.noteStrong,
      noteAfter: picture.noteAfter,
      cards: STAGE_CARDS
    };
  }

  function sellHero(name, picture) {
    var hello = (name ? 'Hi ' + name + ', ' : '') + 'thanks for being honest with us.';
    var lines = {
      profile_occasional: 'You caught this while it’s still an off day, not the whole day. That’s a real step toward feeling better.',
      profile_recurring: 'This has been coming back. Doing something about it now is how that loop starts to loosen.',
      profile_impact: 'It’s already changing your days. Taking it seriously now is how you start getting those days back.'
    };
    if (!picture.placed) {
      return {
        placed: false,
        pill: 'Based on what you shared',
        stageLabel: '',
        title: name ? name + ', you took the first step.' : 'You took the first step.',
        hello: hello,
        body: 'Answering these questions is a real step toward feeling better.',
        highlightsLabel: 'What stood out'
      };
    }
    return {
      placed: true,
      pill: 'Based on what you shared, you’re in',
      stageLabel: picture.focus === 2 ? 'Stage 3' : picture.focus === 1 ? 'Stage 2' : 'Stage 1',
      title: { profile_occasional: 'Occasional discomfort', profile_recurring: 'Discomfort that keeps returning', profile_impact: 'Getting in the way of everyday life' }[picture.selected],
      hello: hello,
      body: lines[picture.selected],
      highlightsLabel: 'What stood out in your answers'
    };
  }

  function helenClose(answers) {
    var topic = {
      bloating: 'bloating',
      bowel_difficulty: 'a gut that won’t move easily',
      loose_urgency: 'loose stools and urgency',
      meal_discomfort: 'discomfort after meals',
      mixed: 'these gut symptoms'
    }[single(answers, 'Q01')];
    if (!topic) {
      return {
        heading: 'Your gut does better when you don’t wait.',
        body: 'Taking care of digestion now is what stops a small pattern from settling in. Being proactive is how this starts to feel better.'
      };
    }
    return {
      heading: 'Don’t let ' + topic + ' become the usual.',
      body: 'Taking care of ' + topic + ' now, and staying with it, is what stops a rough stretch from getting worse. Being proactive is how this starts to feel better.'
    };
  }

  function stageFor(route, profile) {
    if (route === 'education' || route === 'settled') return 'stage_1';
    if (profile === 'profile_impact') return 'stage_3';
    if (profile === 'profile_recurring') return 'stage_2';
    return 'stage_1';
  }

  /* ---------- Expert notes ---------- */

  var HELEN_NOTES = {
    bloating: {
      heading: 'Let’s be precise about what you’re noticing.',
      body: 'Bloating can mean pressure, visible swelling, more gas, or a combination. You’ve helped us separate those details.',
      next: 'Next, we’ll look at when they appear and what happens around meals and bathroom visits.'
    },
    bowel_difficulty: {
      heading: 'Regularity is more than how often you go.',
      body: 'Going every day doesn’t always mean going feels easy or complete. The details you selected help us keep stool consistency, effort, and that unfinished feeling in view.',
      next: 'Next, we’ll explore how this fits into your daily routine.'
    },
    loose_urgency: {
      heading: 'The details of a bathroom visit matter.',
      body: 'We’ll keep the changes you described in view, including urgency or a changing bowel pattern where you selected them.',
      next: 'Next, we’ll look at timing and what happens around bathroom visits.'
    },
    meal_discomfort: {
      heading: 'When the discomfort starts matters.',
      body: 'Feeling full early and feeling uncomfortable after a meal are different experiences. Your guide will reflect the details you selected.',
      next: 'Next, we’ll explore meal timing and the other patterns you’ve noticed.'
    },
    mixed: {
      heading: 'We’ll keep the whole picture in view.',
      body: 'You don’t need to choose one symptom to have your experience taken seriously. We’ll keep the concerns you shared together and explore whether they tend to appear at similar times.',
      next: ''
    }
  };

  function helenNote(answers, forResults) {
    var q01 = single(answers, 'Q01');
    var note = HELEN_NOTES[q01 === 'other' ? 'mixed' : q01] || HELEN_NOTES.mixed;
    var body = note.body;
    if (!forResults && note.next) body += ' ' + note.next;
    return { expert: 'helen', heading: note.heading, body: body };
  }

  function helenSummary(answers) {
    var rows = [];
    var q01 = single(answers, 'Q01');
    if (q01 && MAIN_CONCERN[q01]) rows.push({ label: 'Main concern', value: capitalize(MAIN_CONCERN[q01]) });
    var q03 = single(answers, 'Q03');
    if (q03 === 'days_0') rows.push({ label: 'Past 14 days', value: 'No symptom-days' });
    else if (DAYS[q03]) rows.push({ label: 'Past 14 days', value: DAYS[q03] + ' days' });
    var q04 = single(answers, 'Q04');
    if (DURATION[q04]) rows.push({ label: 'First noticed', value: capitalize(DURATION[q04]) });
    return rows;
  }

  function mayaNote(answers) {
    var flags = concernFlags(answers);
    var q07 = single(answers, 'Q07');
    if (q07 === 'restriction_unclear') {
      return {
        expert: 'maya',
        heading: 'Let’s review what has actually helped.',
        body: 'You’ve removed or reduced foods without being sure it made a difference. Your guide will focus on reviewing those changes and keeping a useful record. If your diet is becoming limited, a dietitian can help you work through it without adding another long list of foods to avoid.'
      };
    }
    if ((flags.bloating || flags.mealDiscomfort) && flags.bowelDifficulty) {
      var aroundMeals = has(answers, 'Q05', 'around_meals') || flags.mealDiscomfort;
      return {
        expert: 'maya',
        heading: 'Meals are only one part of the picture.',
        body: 'You’ve described ' + (aroundMeals ? 'discomfort around meals' : 'bloating') + ' as well as difficult or unfinished bowel movements. Your guide will keep both in view, so the next step isn’t based on food alone.'
      };
    }
    if (q07 === 'repeated') {
      return {
        expert: 'maya',
        heading: 'Let’s make that observation more useful.',
        body: 'You’ve noticed the same foods or drinks being followed by symptoms more than once. Recording the amount, timing, and symptoms can help you describe the pattern clearly. That observation is useful, even though it doesn’t confirm an intolerance.'
      };
    }
    return {
      expert: 'maya',
      heading: 'A clear record is a useful starting point.',
      body: 'You don’t need to have the answer already. Your guide will help you record a few relevant details about meals, symptoms, and bathroom habits so you have something more concrete to work with.'
    };
  }

  function claireNote(answers) {
    var q09 = single(answers, 'Q09');
    var q10 = selected(answers, 'Q10');
    var activities = impactSelected(answers);
    if (has(answers, 'Q10', 'meal_skipping')) {
      return {
        expert: 'claire',
        heading: 'Meals shouldn’t become something you have to avoid.',
        body: 'You’ve shared that worry about symptoms has led you to skip or delay meals. Your guide will prioritise support around eating and the worry attached to it. This is worth discussing with a healthcare professional or dietitian, especially if it’s limiting what or how much you eat.'
      };
    }
    if (q09 === 'days_4_7' || q09 === 'days_8_14' || activities.length) {
      return {
        expert: 'claire',
        heading: 'The planning around symptoms counts too.',
        body: 'Your answers show that your gut has been taking up attention or changing parts of your day. Your guide will include practical guidance focused on the situations you selected. If this is limiting your life, bring that impact into the conversation with a healthcare professional.',
        list: activities.map(function (id) { return IMPACT_SENTENCE[id]; })
      };
    }
    if (single(answers, 'Q08') === 'worse') {
      return {
        expert: 'claire',
        heading: 'Let’s keep that connection in context.',
        body: 'You’ve noticed more discomfort during stressful periods. Your guide will include an introduction to the gut–brain connection, alongside the meal and bathroom observations you shared. That gives us another detail to consider without assuming it explains everything.'
      };
    }
    if ((q09 === 'days_0' || q09 === 'days_1_3') && q10.length === 1 && q10[0] === 'none') {
      return {
        expert: 'claire',
        heading: 'We’ll focus on the physical patterns you reported.',
        body: 'You haven’t reported much worry or disruption to your usual activities. Your guide will focus on the symptom and routine details you shared, with gut–brain information available if you’d like to explore it.'
      };
    }
    return {
      expert: 'claire',
      heading: 'We’ll work with what you’ve chosen to share.',
      body: 'You don’t have to answer every personal question to use your guide. We’ll focus on the information you did share and keep the broader educational material available.'
    };
  }

  function danielNote(answers) {
    var branch = branchOf(answers, 'Q12');
    var q12 = single(answers, 'Q12');
    if (branch === 'Q12A' && q12 === 'settled') {
      return {
        expert: 'daniel',
        heading: 'Keep that steadier feeling going.',
        body: 'You’ve said things have settled. Holding onto that usually means digestion stays steady and your gut stays in balance, instead of slipping back. Your guide will show how to support that.'
      };
    }
    if (branch === 'Q12A' && q12 === 'worse') {
      return {
        expert: 'daniel',
        heading: 'Let’s keep the next step simple.',
        body: 'Not everything you’ve tried has felt good. What stands out is digestion that still needs support, and a gut that isn’t staying balanced. Another stack of products isn’t the answer. Your guide will keep the next step simple.'
      };
    }
    if (branch === 'Q12A' && ['partial_help', 'no_help', 'temporary_help', 'mixed'].indexOf(q12) !== -1) {
      return {
        expert: 'daniel',
        heading: 'Your digestion still needs more support.',
        body: 'You’ve already given a few things a go. Meals can still leave you heavy, bloated, or unfinished, which points to digestion and gut balance that aren’t holding. Your guide will focus on that, without adding another cupboard of products.'
      };
    }
    if (has(answers, 'Q11', 'none')) {
      return {
        expert: 'daniel',
        heading: 'You have a clear place to start.',
        body: 'You don’t need a cupboard full of products. Better digestion and a more balanced gut usually come from one clear kind of daily support. Your guide will show where to start.'
      };
    }
    return {
      expert: 'daniel',
      heading: 'Your gut balance could use more support.',
      body: 'Your routine can stay as it is. What stands out is digestion that could feel steadier, and a gut that isn’t quite in balance. Your guide will fit that into the day you already have.'
    };
  }

  /* ---------- Results content ---------- */

  var PROFILE_CARDS = [
    { id: 'profile_occasional', name: 'Occasional discomfort', line: 'Understanding individual episodes' },
    { id: 'profile_recurring', name: 'Discomfort that keeps returning', line: 'Looking more closely at recurring patterns' },
    { id: 'profile_impact', name: 'Getting in the way of everyday life', line: 'Supporting the activities symptoms are affecting' }
  ];

  var TOPICS = {
    learning: [
      { name: 'How meals fit in', line: 'When you eat, how much, and how quickly can all shape how digestion feels. Noticing these is a useful starting point.' },
      { name: 'What bowel habits can tell you', line: 'How often you go is only one detail. Stool consistency, effort, and whether you feel finished all belong in the picture.' },
      { name: 'Reading a supplement label', line: 'Check the ingredients, amount per serving, directions, and warnings. Bring the label to a pharmacist if you take other medicines or supplements.' }
    ],
    settled: [
      { name: 'What changed', line: 'Note what you changed and roughly when, so the history stays useful if you need it.' },
      { name: 'What you’re still doing', line: 'Keep a short list of the habits or products you’re continuing.' },
      { name: 'If it comes back', line: 'Note when symptoms return, what was happening around meals and bathroom visits, and how long they last.' }
    ],
    no_recent: [
      { name: 'What you’ve experienced', line: 'Keep a note of the symptoms you’ve had and when they tended to appear.' },
      { name: 'What to notice if it returns', line: 'Timing, meals, and bathroom habits are the details most worth recording.' },
      { name: 'Reading a supplement label', line: 'Check the ingredients, amount per serving, directions, and warnings before adding something new.' }
    ]
  };

  var PRIORITIES = {
    individual_review: {
      heading: 'A simple formula can fit your day.',
      body: 'You don’t have to change everything you’re already doing. Gut Reset is two capsules a day, with food, for everyday digestive support.'
    },
    previous_worsening: {
      heading: 'A simpler next step.',
      body: 'Some of what you tried didn’t feel right. Gut Reset is one papaya seed formula for daily digestive support, taken with food.'
    },
    eating_support: {
      heading: 'Make room for support around eating.',
      body: 'Tell a healthcare professional or dietitian that worry about symptoms has led you to skip or delay meals. Include what this has changed in your day.'
    },
    restriction_review: {
      heading: 'Review what you’ve cut out.',
      body: 'List what you removed, when you changed it, and whether anything clearly improved. If your diet is narrowing, seek dietitian guidance before removing more foods.'
    },
    daily_impact: {
      heading: 'Bring the everyday impact into the conversation.',
      body: 'Include the activities or plans affected by symptoms or worry. Those details belong alongside the physical symptoms you describe.'
    },
    food_observation: {
      heading: 'Make food observations more specific.',
      body: 'Record the food or drink, amount, timing, and symptoms. Keep an observation separate from a confirmed intolerance.'
    },
    bowel_record: {
      heading: 'Record the bathroom details that matter to you.',
      body: ''
    },
    symptom_timing: {
      heading: 'Keep a short, focused record.',
      body: 'Note when symptoms occur and what was happening around meals or bathroom visits. Start with a few relevant details rather than tracking everything.'
    },
    previous_attempts: {
      heading: 'Bring your attempts together.',
      body: 'List what you tried, for how long, and what you noticed. If several things changed at once, say so.'
    },
    stress_observation: {
      heading: 'Include stressful periods in the picture.',
      body: 'Note the connection you observed alongside meals and bowel habits. It is one part of the record, not a confirmed explanation.'
    },
    maintenance: {
      heading: 'Keep that steadier feeling going.',
      body: 'A daily digestive formula is an easy way to support the calmer stretch you’ve found. Two capsules of Gut Reset a day, with food.'
    },
    general_learning: {
      heading: 'Build a useful starting point.',
      body: 'Explore the guide’s explanations of meals, bowel habits, and supplement labels. Seek advice if a new concern develops.'
    }
  };

  function bowelRecordBody(flags) {
    var parts = [];
    if (flags.bowelDifficulty) parts.push('stool consistency', 'effort', 'completeness');
    if (flags.looseUrgency) {
      if (parts.indexOf('stool consistency') === -1) parts.push('stool consistency');
      parts.push('urgency');
    }
    return 'Note ' + joinList(parts) + ' according to the concerns you selected.';
  }

  function priorityList(answers, route, flags, review) {
    var ids = [];
    function add(id) { if (ids.indexOf(id) === -1 && ids.length < 3) ids.push(id); }
    if (route === 'education') add('general_learning');
    if (route === 'settled') add('maintenance');
    if (review.individualReview) add('individual_review');
    if (review.previousWorsening) add('previous_worsening');
    if (route !== 'education') {
      if (has(answers, 'Q10', 'meal_skipping')) add('eating_support');
      if (single(answers, 'Q07') === 'restriction_unclear') add('restriction_review');
      if (hasAny(answers, 'Q10', SIGNIFICANT_IMPACT) || single(answers, 'Q09') === 'days_8_14') add('daily_impact');
      var q07 = single(answers, 'Q07');
      if (q07 === 'repeated' || q07 === 'inconsistent') add('food_observation');
      if (flags.bowel) add('bowel_record');
      if (hasAny(answers, 'Q11', ['diet_routine', 'fibre', 'probiotic', 'enzymes', 'botanicals', 'medicines', 'other'])) add('previous_attempts');
      if (route === 'symptomatic' || route === 'no_recent' || route === 'insufficient') add('symptom_timing');
      if (single(answers, 'Q08') === 'worse') add('stress_observation');
    }
    return ids.map(function (id) {
      var item = PRIORITIES[id];
      return {
        id: id,
        heading: item.heading,
        body: id === 'bowel_record' ? bowelRecordBody(flags) : item.body
      };
    });
  }

  function timingHighlight(answers, past) {
    var picked = selected(answers, 'Q05');
    if (!picked.length) return '';
    if (picked[0] === 'no_pattern') return past ? 'You hadn’t noticed a clear timing pattern.' : 'You haven’t noticed a clear timing pattern yet.';
    var phrases = picked.map(function (id) { return TIMING[id]; }).filter(Boolean);
    if (!phrases.length) return '';
    return (past ? 'When you’ve had symptoms, you noticed them most ' : 'You notice symptoms most ') + joinList(phrases) + '.';
  }

  function bowelHighlight(answers) {
    var branch = branchOf(answers, 'Q06');
    var value = single(answers, 'Q06');
    if (branch === 'Q06A') return AFTER_BOWEL[value] || '';
    if (branch === 'Q06B') return BOWEL_PATTERN[value] || '';
    return '';
  }

  function highlights(answers, route) {
    if (route === 'education') return TOPICS.learning.map(function (topic) { return topic.name + '.'; });
    var rows = [];
    var q01 = single(answers, 'Q01');
    if (MAIN_CONCERN[q01]) rows.push('Your main concern: ' + MAIN_CONCERN[q01] + '.');
    var q03 = single(answers, 'Q03');
    if (q03 === 'days_0') rows.push('Recent frequency: no symptom-days in the past 14 days.');
    else if (DAYS[q03]) rows.push('Recent frequency: ' + DAYS[q03] + ' of the past 14 days.');
    var impact = impactSelected(answers);
    var third = '';
    if (impact.length) third = IMPACT_SENTENCE[impact[0]];
    if (!third) third = bowelHighlight(answers);
    if (!third) third = timingHighlight(answers, q03 === 'days_0');
    if (!third && DURATION[single(answers, 'Q04')]) third = 'You first noticed this ' + DURATION[single(answers, 'Q04')] + '.';
    if (third) rows.push(third);
    return rows.slice(0, 3);
  }

  function hero(answers, route, profile, name) {
    var q03 = single(answers, 'Q03');
    var days = DAYS[q03];
    var impact = impactSelected(answers);
    var q09 = single(answers, 'Q09');
    if (route === 'education') {
      return {
        headline: withName(name, 'get to know your gut a little better.'),
        body: ['You’re here to learn, rather than explore current symptoms. Your guide introduces useful digestive-health concepts, what to pay attention to, and questions worth asking when exploring support.']
      };
    }
    if (route === 'settled') {
      return {
        headline: withName(name, 'let’s keep track of what’s working.'),
        body: ['You said your symptoms have settled and haven’t returned. Your guide focuses on what changed, what you’re still doing, and what to keep in view.']
      };
    }
    if (route === 'no_recent') {
      var body = ['You reported no symptom-days in the past 14 days. Your guide keeps your earlier experiences in view and explains what may be useful to notice if the pattern returns.'];
      if (impact.length) {
        body.push('You also shared that symptoms or worry about them have changed ' + joinList(impact.map(function (id) { return IMPACT_PHRASE[id]; })) + '.');
      } else if (WORRY_DAYS[q09]) {
        body.push('You also reported worrying that your gut might disrupt plans on ' + WORRY_DAYS[q09] + ' of the past 14 days.');
      }
      return { headline: withName(name, 'let’s make sense of the pattern you’ve experienced.'), body: body };
    }
    if (route === 'symptomatic' && profile === 'profile_impact') {
      if (impact.length) {
        return {
          headline: withName(name, 'your gut is affecting more than your digestion.'),
          body: ['You shared that symptoms or worries about them have changed ' + joinList(impact.map(function (id) { return IMPACT_PHRASE[id]; })) + '. Your guide keeps that impact alongside the physical details you reported, with practical priorities focused on both.']
        };
      }
      return {
        headline: withName(name, 'your gut is affecting more than your digestion.'),
        body: ['You reported worrying that your gut might disrupt plans on 8–14 of the past 14 days. Your guide addresses that worry alongside the symptom details you shared.']
      };
    }
    if (route === 'symptomatic' && profile === 'profile_recurring') {
      return {
        headline: withName(name, 'your gut discomfort keeps coming back.'),
        body: ['You reported symptoms on ' + days + ' of the past 14 days. Your guide brings your main concern, daily patterns, and previous experiences together so your next step starts with a clearer picture.']
      };
    }
    if (route === 'symptomatic' && profile === 'profile_occasional') {
      return {
        headline: withName(name, 'let’s understand when your gut feels off.'),
        body: ['You reported symptoms on ' + days + ' of the past 14 days, with little reported worry or change to your usual activities. Your guide starts with the details around those episodes and what would be useful to notice next.']
      };
    }
    return {
      headline: withName(name, 'let’s bring your gut experiences together.'),
      body: ['Your guide reflects the answers you chose to share, with relevant information to explore and clear next steps for the details that remain uncertain.']
    };
  }

  function heroNote(answers, route, profile, review) {
    if (route === 'education') {
      return {
        expert: 'daniel',
        heading: 'A useful place to start.',
        body: 'Papaya Seeds Gut Reset is a simple daily formula for digestive support. Two capsules a day, with food, whenever you want a little help with how meals sit.'
      };
    }
    if (route === 'settled' || review.individualReview) return danielNote(answers);
    if (profile === 'profile_impact') return claireNote(answers);
    return helenNote(answers, true);
  }

  function detailValues(answers) {
    var branch = branchFor('Q02', answers);
    var map = DETAIL[branch] || {};
    return selected(answers, 'Q02').map(function (id) { return map[id]; }).filter(Boolean);
  }

  function explanation(answers, route) {
    var flags = concernFlags(answers);
    var q01 = single(answers, 'Q01');
    var timingList = selected(answers, 'Q05').filter(function (id) { return TIMING[id]; }).map(function (id) { return TIMING[id]; });
    var timing = timingList.length ? [capitalize(timingList[0])].concat(timingList.slice(1)) : [];
    var bowel = bowelHighlight(answers);
    var details = detailValues(answers);
    var q03 = single(answers, 'Q03');
    var impact = impactSelected(answers);
    var card = [];
    function row(label, value) { if (value) card.push({ label: label, value: value }); }

    if (route === 'education') {
      return {
        heading: 'Useful things to notice about digestion.',
        body: ['Explore how meals, bowel habits, and everyday experiences can fit into a useful digestive-health record. You don’t need to start tracking symptoms you don’t have.'],
        card: []
      };
    }
    if (route === 'settled') {
      row('Main concern', MAIN_CONCERN[q01] ? capitalize(MAIN_CONCERN[q01]) : '');
      row('First noticed', DURATION[single(answers, 'Q04')] ? capitalize(DURATION[single(answers, 'Q04')]) : '');
      return {
        heading: 'What changed—and what’s working now.',
        body: ['You reported that symptoms have settled. Keep a note of the changes you made and what you’re continuing, so that history remains useful if you need it.'],
        card: card
      };
    }

    if (q01 === 'bloating') {
      var bloatBody = ['You selected bloating or gas as your main concern. Your answers help distinguish pressure, visible swelling, and gas-related symptoms where you reported them. Your guide looks at when this happens and what else you notice alongside it.'];
      if (flags.bowelDifficulty) bloatBody.push('You also described difficult or unfinished bowel movements, so your guide keeps meals and bathroom habits in view together.');
      row('Main experience', capitalize(joinList(details)));
      row('Reported timing', joinList(timing));
      row('Bowel detail', bowel);
      return { heading: 'Let’s separate the feeling from the pattern.', body: bloatBody, card: card };
    }
    if (q01 === 'bowel_difficulty') {
      var examples = [];
      if (has(answers, 'Q02', 'hard_stool')) examples.push('stool consistency');
      if (has(answers, 'Q02', 'straining')) examples.push('effort');
      if (has(answers, 'Q02', 'incomplete')) examples.push('completeness');
      var bowelBody = examples.length
        ? 'You selected feeling backed up or unfinished as your main concern. Your guide focuses on the details you reported—such as ' + joinList(examples) + '—alongside how often symptoms occur.'
        : 'You selected feeling backed up or unfinished as your main concern. Your guide focuses on the details you reported.';
      row('Reported bowel details', capitalize(joinList(details)));
      row('Symptom-days', q03 === 'days_0' ? 'None in the past 14 days' : DAYS[q03] ? DAYS[q03] + ' of the past 14 days' : '');
      row('After a bowel movement', branchOf(answers, 'Q06') === 'Q06A' ? bowel : '');
      return { heading: 'Your bathroom experience is about more than frequency.', body: [bowelBody], card: card };
    }
    if (q01 === 'loose_urgency') {
      row('Reported details', capitalize(joinList(details)));
      row('Reported timing', joinList(timing));
      row('Everyday impact', impact.length ? impact.map(function (id) { return IMPACT_SENTENCE[id]; }).join(' ') : '');
      return {
        heading: 'Let’s make the changes easier to describe.',
        body: ['You selected loose stools or urgent bathroom visits as your main concern. Your guide keeps stool changes, urgency, and changing bowel habits separate where you reported them, so you have a clearer description of what has been happening.'],
        card: card,
        help: true
      };
    }
    if (q01 === 'meal_discomfort') {
      var mealTiming = [];
      if (has(answers, 'Q05', 'around_meals')) mealTiming.push('During or soon after meals');
      if (has(answers, 'Q05', 'later_day')) mealTiming.push('Later in the day, after several meals');
      row('Around meals', mealTiming.join(' · '));
      row('Reported detail', capitalize(joinList(details)));
      row('Also noticed', bowel);
      return {
        heading: 'When you feel full or uncomfortable matters.',
        body: ['You selected discomfort around meals as your main concern. Your guide distinguishes feeling full early from discomfort after eating where you reported those details, then puts that experience alongside timing and any bowel changes.'],
        card: card
      };
    }
    if (q01 === 'mixed') {
      selected(answers, 'Q02').slice(0, 3).forEach(function (id, index) {
        row(index === 0 ? 'You selected' : '', DETAIL.Q02E[id]);
      });
      return {
        heading: 'There’s more than one part to your experience.',
        body: ['You selected several concerns. Your guide keeps them together and looks at the details you reported about timing, bathroom habits, meals, and everyday impact. We won’t assume they all have the same explanation.'],
        card: card
      };
    }
    var description = text(answers, 'Q02', 'other');
    row('In your words', description ? '“' + description + '”' : '');
    selected(answers, 'Q02').filter(function (id) { return id !== 'other'; }).slice(0, 2).forEach(function (id) {
      row('Also noticed', DETAIL.Q02E[id]);
    });
    return {
      heading: 'We’ll stay close to what you shared.',
      body: ['Your main concern doesn’t fit neatly into the listed options. Your guide keeps your description and any structured observations together, with general information and questions that can help you describe the experience more clearly.'],
      card: card
    };
  }

  function profileRow(route, profile) {
    if (route === 'symptomatic' && profile) {
      return { mode: 'profiles', selected: profile, items: PROFILE_CARDS };
    }
    if (route === 'settled') return { mode: 'topics', items: TOPICS.settled };
    if (route === 'no_recent') return { mode: 'topics', items: TOPICS.no_recent };
    return { mode: 'topics', items: TOPICS.learning };
  }

  function commercial(route, review) {
    if (route === 'education') return 'education';
    if (review.individualReview) return 'review';
    if (review.previousWorsening) return 'worsening';
    if (route === 'settled') return 'settled';
    if (review.suitabilityIncomplete) return 'incomplete';
    return 'standard';
  }

  var SUPPORT_COPY = {
    review: {
      heading: 'A little daily support',
      body: 'Papaya Seeds Gut Reset supports digestion as a simple habit. Two capsules a day, with food, alongside the routine you already have.'
    },
    settled: {
      heading: 'A little daily support',
      body: 'Things have settled. Gut Reset is an easy way to keep supporting digestion. Two capsules a day, with food.'
    },
    education: {
      heading: 'A little daily support',
      body: 'Papaya Seeds Gut Reset is one daily formula for digestive support. Two capsules a day, with food.'
    },
    incomplete: {
      heading: 'A little daily support',
      body: 'Papaya Seeds Gut Reset is one daily formula for digestive support. Two capsules a day, with food.'
    },
    worsening: {
      heading: 'A little daily support',
      body: 'Gut Reset is a straightforward papaya seed formula for everyday digestive support. Two capsules a day, with food.'
    },
    standard: {
      heading: 'A little daily support',
      body: 'Papaya Seeds Gut Reset supports digestion when meals leave you heavy, bloated, or unfinished. Two capsules a day, with food.'
    }
  };

  var BANNERS = {
    conversation: {
      heading: 'Make your next conversation easier.',
      body: 'Save the details you shared, including your symptoms, previous attempts, and the circumstances worth discussing before adding digestive support.',
      cta: 'Save my symptom summary'
    },
    settled: {
      heading: 'Keep track of what’s working.',
      body: 'Save a record of the changes you reported and what you’re continuing, so you can return to it when you need it.',
      cta: 'Save my summary'
    },
    education: {
      heading: 'Keep your starting point close.',
      body: 'Return to your guide’s explanations and questions whenever you want to explore digestive health or product information.',
      cta: 'Save my guide'
    }
  };

  function bannerFor(mode) {
    if (mode === 'education') return BANNERS.education;
    if (mode === 'settled') return BANNERS.settled;
    if (mode === 'review' || mode === 'worsening' || mode === 'incomplete') return BANNERS.conversation;
    return null;
  }

  function summaryRows(state) {
    var answers = state.answers;
    var rows = [];
    ['Q01', 'Q02', 'Q03', 'Q04', 'Q05', 'Q06', 'Q07', 'Q08', 'Q09', 'Q10', 'Q11', 'Q12', 'Q13'].forEach(function (slot) {
      var stored = entry(answers, slot);
      if (!stored || !stored.selected || !stored.selected.length) return;
      var q = question(slot, answers);
      if (!q) return;
      var labels = stored.selected.map(function (id) {
        var option = q.options.filter(function (item) { return item.id === id; })[0];
        if (!option) return '';
        var extra = stored.text && stored.text[id] ? ' (' + stored.text[id].trim() + ')' : '';
        return option.label + extra;
      }).filter(Boolean);
      if (stored.text && stored.text._note) labels.push('Note: ' + stored.text._note.trim());
      rows.push({ question: q.text, answers: labels });
    });
    return rows;
  }

  function triedSeveral(answers) {
    return selected(answers, 'Q11').filter(function (id) {
      return ['fibre', 'probiotic', 'enzymes', 'botanicals', 'medicines', 'other'].indexOf(id) !== -1;
    }).length >= 2;
  }

  function derive(state) {
    var answers = state.answers;
    var name = (state.firstName || '').trim();
    var route = routeFor(answers);
    var profile = route === 'education' ? null : profileFor(answers);
    var flags = concernFlags(answers);
    var review = reviewFlags(answers);
    var stage = stageFor(route, profile);
    var mode = commercial(route, review);
    var note = heroNote(answers, route, profile, review);
    var heroContent = hero(answers, route, profile, name);
    return {
      route: route,
      profile: profile,
      stage: stage,
      stageName: STAGE_NAMES[stage],
      mainConcern: single(answers, 'Q01'),
      concerns: route === 'education' ? [] : concernList(answers),
      flags: flags,
      review: review,
      commercial: mode,
      announcement: route === 'education'
        ? 'Your INNERDAY Gut Guide · A starting point for learning'
        : 'Your INNERDAY Gut Guide · Based on what you shared',
      hero: {
        eyebrow: 'Based on what you shared',
        headline: heroContent.headline,
        body: heroContent.body,
        highlightsLabel: route === 'education' ? 'What your guide covers' : 'What stood out in your answers',
        highlights: highlights(answers, route),
        note: note,
        noteList: note.list || []
      },
      sell: sellHero(name, stagePicture(route, profile, stage)),
      helenClose: helenClose(answers),
      explanation: explanation(answers, route),
      stagePicture: stagePicture(route, profile, stage),
      profileRow: profileRow(route, profile),
      priorities: priorityList(answers, route, flags, review),
      support: mode === 'standard' && triedSeveral(answers)
        ? { heading: SUPPORT_COPY.standard.heading, body: 'You’ve already tried a few things. Gut Reset is one daily formula for digestive support. Two capsules a day, with food.' }
        : SUPPORT_COPY[mode],
      banner: bannerFor(mode),
      summary: summaryRows(state)
    };
  }

  /* ---------- Screens ---------- */

  function screens(state) {
    var answers = state.answers;
    var list = ['name', 'birth', 'intro_1', 'Q01'];
    if (isEducation(answers)) return list.concat(['loading', 'email']);
    list = list.concat(['Q02', 'Q03', 'Q04', 'note_1', 'intro_2', 'Q05', 'Q06', 'Q07', 'note_2', 'intro_3', 'Q08', 'Q09', 'Q10', 'note_3', 'intro_4', 'Q11', 'Q12', 'Q13', 'note_4', 'loading', 'email']);
    return list;
  }

  function slotPosition(slot) {
    for (var i = 0; i < PARTS.length; i += 1) {
      var index = PARTS[i].questions.indexOf(slot);
      if (index !== -1) return { part: PARTS[i].part, index: index + 1, total: PARTS[i].questions.length, expert: PARTS[i].expert };
    }
    return null;
  }

  function answered(state, slot) {
    var stored = entry(state.answers, slot);
    return Boolean(stored && stored.selected && stored.selected.length);
  }

  /* ---------- Delivery ---------- */

  function klaviyoProperties(state, derived, guideUrl) {
    return {
      quiz_completed: true,
      quiz_stage: derived.stage,
      quiz_stage_name: derived.stageName,
      quiz_route: derived.route,
      quiz_profile: derived.profile || 'unassigned',
      quiz_main_concern: derived.mainConcern || 'none',
      quiz_concerns: derived.concerns,
      quiz_review_needed: derived.review.individualReview,
      quiz_previous_worsening: derived.review.previousWorsening,
      quiz_result_id: state.result.id,
      quiz_completed_at: state.result.createdAt,
      quiz_version: QUIZ_VERSION,
      quiz_guide_page: guideUrl
    };
  }

  function klaviyoRequest(config, path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(config.klaviyoKey), {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2024-10-15' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo ' + path + ' ' + response.status);
      return true;
    });
  }

  function sendToKlaviyo(config, state, derived, requestType) {
    if (!config.klaviyoKey) return Promise.reject(new Error('Klaviyo is not configured'));
    var guideUrl = window.location.origin + config.guidePath + (config.guidePath.indexOf('?') === -1 ? '?' : '&') + 'r=' + encodeURIComponent(state.result.id);
    var properties = klaviyoProperties(state, derived, guideUrl);
    var profile = {
      email: state.result.email,
      first_name: state.firstName || undefined,
      properties: properties
    };
    var eventProperties = {};
    Object.keys(properties).forEach(function (key) { eventProperties[key.replace(/^quiz_/, '')] = properties[key]; });
    eventProperties.request_type = requestType;
    var saveProfile = klaviyoRequest(config, 'events', {
      data: {
        type: 'event',
        attributes: {
          properties: eventProperties,
          unique_id: state.result.id + ':' + requestType + (requestType === 'initial' || requestType === 'update' ? '' : ':' + Date.now()),
          metric: { data: { type: 'metric', attributes: { name: 'Quiz Completed' } } },
          profile: { data: { type: 'profile', attributes: profile } }
        }
      }
    });
    if (!config.klaviyoList) return saveProfile;
    return saveProfile.then(function () {
      return klaviyoRequest(config, 'subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Gut quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: state.result.email }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: config.klaviyoList } } }
        }
      });
    });
  }

  function supabaseHeaders(config, extra) {
    var headers = { apikey: config.supabaseKey, 'content-type': 'application/json' };
    if (/^eyJ/.test(config.supabaseKey)) headers.authorization = 'Bearer ' + config.supabaseKey;
    Object.keys(extra).forEach(function (key) { headers[key] = extra[key]; });
    return headers;
  }

  function sendToSupabase(config, state, derived) {
    if (!config.supabaseUrl || !config.supabaseKey) return Promise.reject(new Error('Supabase is not configured'));
    var row = {
      result_id: state.result.id,
      session_id: state.sessionId,
      quiz_version: QUIZ_VERSION,
      routing_version: ROUTING_VERSION,
      content_version: CONTENT_VERSION,
      notice_version: state.consent ? state.consent.noticeVersion : null,
      health_consent_at: state.consent ? state.consent.at : null,
      marketing_subscribed: true,
      first_name: state.firstName || null,
      email: state.result.email,
      birth_year: state.birthYear,
      route: derived.route,
      stage: derived.stage,
      profile: derived.profile,
      main_concern: derived.mainConcern,
      concerns: derived.concerns,
      review_needed: derived.review.individualReview,
      previous_worsening: derived.review.previousWorsening,
      suitability_incomplete: derived.review.suitabilityIncomplete,
      answers: state.answers
    };
    return fetch(config.supabaseUrl.replace(/\/$/, '') + '/rest/v1/gut_quiz_responses', {
      method: 'POST',
      keepalive: true,
      headers: supabaseHeaders(config, { prefer: 'return=minimal' }),
      body: JSON.stringify(row)
    }).then(function (response) {
      if (!response.ok) throw new Error('Supabase ' + response.status);
      return true;
    });
  }

  function fetchFromSupabase(config, resultId) {
    if (!config.supabaseUrl || !config.supabaseKey) return Promise.reject(new Error('Supabase is not configured'));
    return fetch(config.supabaseUrl.replace(/\/$/, '') + '/rest/v1/rpc/get_gut_quiz_result', {
      method: 'POST',
      headers: supabaseHeaders(config, {}),
      body: JSON.stringify({ p_result_id: resultId })
    }).then(function (response) {
      if (!response.ok) throw new Error('Supabase ' + response.status);
      return response.json();
    }).then(function (row) {
      if (Array.isArray(row)) row = row[0];
      if (!row || !row.answers) throw new Error('Result not found');
      var state = emptyState();
      state.sessionId = row.session_id || state.sessionId;
      state.firstName = row.first_name || '';
      state.birthYear = row.birth_year || null;
      state.answers = row.answers;
      state.screen = 'email';
      state.result = {
        id: resultId,
        email: row.email || '',
        hash: '',
        createdAt: row.created_at,
        quizVersion: row.quiz_version,
        emailState: 'delivered',
        lastRequestedAt: 0
      };
      return state;
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var done = false;
      var timer = setTimeout(function () {
        if (!done) { done = true; resolve('timeout'); }
      }, ms);
      promise.then(function () {
        if (!done) { done = true; clearTimeout(timer); resolve('ok'); }
      }, function () {
        if (!done) { done = true; clearTimeout(timer); resolve('failed'); }
      });
    });
  }

  function bindHelp(scope) {
    var dialog = document.querySelector('[data-gq-help]');
    if (!dialog || !scope) return;
    scope.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-gq-help-open]');
      if (!trigger) return;
      event.preventDefault();
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    });
    if (dialog.dataset.bound === 'true') return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-gq-help-close]')) dialog.close();
    });
  }

  window.InnerdayGutQuiz = {
    sendToKlaviyo: sendToKlaviyo,
    sendToSupabase: sendToSupabase,
    fetchFromSupabase: fetchFromSupabase,
    withTimeout: withTimeout,
    bindHelp: bindHelp,
    QUIZ_VERSION: QUIZ_VERSION,
    ROUTING_VERSION: ROUTING_VERSION,
    CONTENT_VERSION: CONTENT_VERSION,
    NOTICE_VERSION: NOTICE_VERSION,
    EXPERTS: EXPERTS,
    PARTS: PARTS,
    STAGE_NAMES: STAGE_NAMES,
    emptyState: emptyState,
    load: load,
    save: save,
    clear: clear,
    uid: uid,
    question: question,
    setAnswer: setAnswer,
    selected: selected,
    single: single,
    entry: entry,
    answered: answered,
    branchFor: branchFor,
    screens: screens,
    slotPosition: slotPosition,
    helenNote: helenNote,
    helenSummary: helenSummary,
    mayaNote: mayaNote,
    claireNote: claireNote,
    danielNote: danielNote,
    derive: derive,
    withName: withName
  };
})();
